<template>
  <section>
    <div
      :class="[
        `bg-${bgColor}`,
        {
          'bg-gradient-to-r from-transparent to-[#880019]':
            bgColor === 'primary',
        },
      ]"
    >
      <div class="relative py-spacebetween">
        <div
          v-if="isWideScreen"
          class="absolute inset-0 z-10 flex *:flex-1 *:cursor-pointer"
        >
          <div @click="scrollToImage('left')"></div>
          <div @click="scrollToImage('right')"></div>
        </div>
        <div v-else class="absolute inset-0 z-10 xm:px-sidespacing">
          <div
            class="relative flex size-full items-center justify-between px-2 *:cursor-pointer"
          >
            <img
              @click="scrollToImage('left')"
              class="z-30 aspect-square h-8 -scale-x-100 rounded-full border bg-white/50 p-1"
              src="/web/assets/appletwig-red.svg"
              alt="nach links scrollen"
            />
            <img
              @click="scrollToImage('right')"
              class="z-30 aspect-square h-8 w-auto rounded-full border bg-white/50 p-1"
              src="/web/assets/appletwig-red.svg"
              alt="nach rechts scrollen"
            />
          </div>
        </div>
        <div
          class="animate-slide-left flex flex-row gap-8 overflow-x-hidden px-sidespacing xm:px-0"
          ref="image-container"
        >
          <div
            v-for="(image, index) in galleryData?.images"
            :key="`image-${index}`"
            class="relative h-[70vh] max-h-[70vw] w-[80vw] flex-shrink-0 overflow-hidden rounded-3xl xm:h-80 xm:w-full xm:px-sidespacing"
            ref="image-element"
          >
            <img
              :src="image.src"
              :alt="image.alt || `Gallery image ${index + 1}`"
              :style="image.focalStyle || undefined"
              class="size-full object-cover xm:rounded-3xl"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue';
import { useMediaQuery, useResizeObserver, useScroll } from '@vueuse/core';

interface GalleryImage {
  src: string;
  alt?: string;
  focalStyle?: string;
}
interface GalleryData {
  bgColor: string;
  images: GalleryImage[];
}

const props = defineProps<{
  galleryData?: GalleryData;
}>();

const bgColor = props.galleryData?.bgColor || 'white';

const isWideScreen = useMediaQuery('(min-width: 625px)');

const imageContainer = useTemplateRef('image-container');
const imageElement = useTemplateRef('image-element');

const imageCount = props.galleryData?.images.length ?? 0;
const { x: scrollX } = useScroll(imageContainer, { behavior: 'smooth' });
const currentIndex = ref(0);

const getTargetScroll = (index: number) => {
  if (!imageElement.value || !imageContainer.value) return null;
  const container = imageContainer.value;
  const containerWidth = container.getBoundingClientRect().width;
  const imageWidth = imageElement.value[0].getBoundingClientRect().width;
  const imageGap = Number.parseFloat(getComputedStyle(container).gap || '0') || 0; //prettier-ignore
  const paddingLeft = Number.parseFloat(getComputedStyle(container).paddingLeft || '0'); //prettier-ignore
  return index * (imageWidth + imageGap) - (containerWidth - imageWidth) / 2 + paddingLeft; //prettier-ignore
};

const scrollToImage = (direction: 'left' | 'right') => {
  if (!imageElement.value || !imageContainer.value) return;
  const nextIndex =
    direction === 'right'
      ? Math.min(imageCount - 1, currentIndex.value + 1)
      : Math.max(0, currentIndex.value - 1);
  currentIndex.value = nextIndex;
  const target = getTargetScroll(nextIndex);
  if (target !== null) scrollX.value = target;
};

useResizeObserver(imageContainer, () => {
  const target = getTargetScroll(currentIndex.value);
  if (target !== null && imageContainer.value) {
    imageContainer.value.scrollLeft = target;
  }
});
</script>
